import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Mail, MailOpen, Reply } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminInbox({ onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/messages");
      setMessages(data);
      onUnreadChange?.(data.filter((m) => !m.read).length);
    } catch {
      toast.error("Failed to load messages.");
    } finally {
      setLoading(false);
    }
  }, [onUnreadChange]);

  useEffect(() => {
    load();
  }, [load]);

  const openMessage = async (m) => {
    const next = openId === m.id ? null : m.id;
    setOpenId(next);
    if (next && !m.read) {
      try {
        await api.patch(`/messages/${m.id}/read`);
        setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
        setMessages((prev) => {
          onUnreadChange?.(prev.filter((x) => !x.read).length);
          return prev;
        });
      } catch {
        /* ignore */
      }
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/messages/${deleteTarget.id}`);
      toast.success("Message deleted.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed.");
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return <div className="py-20 text-center font-mono text-sm text-onyx-muted">Loading inbox…</div>;
  }

  if (messages.length === 0) {
    return (
      <div className="border border-[#DCDEE6] bg-white/40 px-5 py-16 text-center" data-testid="inbox-empty">
        <Mail className="w-6 h-6 mx-auto text-onyx-muted mb-3" strokeWidth={1.5} />
        <p className="font-serif text-2xl text-onyx-muted">No enquiries yet.</p>
        <p className="font-sans text-sm text-onyx-muted mt-2">
          Messages sent via the contact form will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-[#DCDEE6] bg-white/40" data-testid="inbox-list">
      {messages.map((m) => (
        <div key={m.id} className="border-b border-[#E7E9F0] last:border-b-0">
          <button
            onClick={() => openMessage(m)}
            data-testid={`inbox-item-${m.id}`}
            className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-white/50 transition-colors"
          >
            {m.read ? (
              <MailOpen className="w-4 h-4 text-onyx-muted shrink-0" strokeWidth={1.5} />
            ) : (
              <Mail className="w-4 h-4 text-ochre shrink-0" strokeWidth={2} />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`font-sans text-sm ${m.read ? "text-onyx-muted" : "text-onyx font-semibold"}`}>
                  {m.name}
                </span>
                {!m.read && (
                  <span className="font-mono text-[9px] tracking-wider uppercase bg-ochre text-alabaster px-1.5 py-0.5">
                    New
                  </span>
                )}
              </div>
              <div className="font-serif text-lg text-onyx truncate">{m.subject}</div>
            </div>
            <span className="font-mono text-[10px] tracking-wider uppercase text-onyx-muted shrink-0 hidden sm:block">
              {fmtDate(m.created_at)}
            </span>
          </button>

          {openId === m.id && (
            <div className="px-5 pb-6 pt-1 bg-white/30" data-testid={`inbox-detail-${m.id}`}>
              <div className="grid sm:grid-cols-2 gap-2 font-sans text-sm text-onyx-muted mb-4">
                <div>
                  <span className="font-mono text-[10px] tracking-wider uppercase text-ochre">From</span>
                  <div className="text-onyx">{m.name}</div>
                </div>
                <div>
                  <span className="font-mono text-[10px] tracking-wider uppercase text-ochre">Email</span>
                  <div className="text-onyx">{m.email}</div>
                </div>
              </div>
              <p className="font-sans text-base leading-relaxed text-onyx whitespace-pre-wrap border-l-2 border-ochre pl-4">
                {m.message}
              </p>
              <div className="flex items-center gap-4 mt-5">
                <a
                  href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                  data-testid={`inbox-reply-${m.id}`}
                  className="flex items-center gap-2 font-sans uppercase text-xs tracking-[0.2em] bg-onyx text-alabaster px-5 py-2.5 hover:bg-ochre transition-colors"
                >
                  <Reply className="w-3.5 h-3.5" /> Reply
                </a>
                <button
                  onClick={() => setDeleteTarget(m)}
                  data-testid={`inbox-delete-${m.id}`}
                  className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-onyx-muted hover:text-destructive"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-alabaster" data-testid="inbox-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete message?</AlertDialogTitle>
            <AlertDialogDescription>
              This enquiry from {deleteTarget?.name} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="inbox-delete-confirm"
              className="rounded-none bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
